// Dates in the inbox use the clinic's time zone, not the browser's.

export interface WindowState {
  open: boolean;
  hours: number;
  minutes: number;
}

/** Time left in the 24 h free-reply window. */
export const windowRemaining = (closesAt: string | null, nowMs: number): WindowState => {
  if (!closesAt) return { open: false, hours: 0, minutes: 0 };
  const ms = Date.parse(closesAt) - nowMs;
  if (!(ms > 0)) return { open: false, hours: 0, minutes: 0 };
  const totalMinutes = Math.ceil(ms / 60_000);
  return { open: true, hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
};

/** "21 h 14 min", "35 min". */
export const formatRemaining = (w: WindowState): string => {
  if (w.hours === 0) return `${w.minutes} min`;
  return w.minutes === 0 ? `${w.hours} h` : `${w.hours} h ${w.minutes} min`;
};

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

/** How long ago an instant was: "hace 35 min", "hace 3 h", "hace 2 d". Null when there is no instant or it is in the future. */
export const elapsedLabel = (iso: string | null, nowMs: number): string | null => {
  if (!iso) return null;
  const ms = nowMs - Date.parse(iso);
  if (!(ms >= 0)) return null;
  if (ms < 60_000) return 'hace menos de 1 min';
  if (ms < HOUR_MS) return `hace ${Math.floor(ms / 60_000)} min`;
  if (ms < 2 * DAY_MS) return `hace ${Math.floor(ms / HOUR_MS)} h`;
  return `hace ${Math.floor(ms / DAY_MS)} d`;
};

/** The calendar day ("YYYY-MM-DD") of an instant in a time zone. */
export const dayKey = (ms: number, timeZone: string): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(ms);

const previousDay = (key: string): string => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
};

/** "14:09". */
export const timeLabel = (iso: string, timeZone: string): string =>
  new Intl.DateTimeFormat('es-GT', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(
    Date.parse(iso),
  );

/** The separator of the thread: "Hoy", "Ayer" or "7 de octubre". */
export const dayLabel = (iso: string, timeZone: string, nowMs: number): string => {
  const day = dayKey(Date.parse(iso), timeZone);
  const today = dayKey(nowMs, timeZone);
  if (day === today) return 'Hoy';
  if (day === previousDay(today)) return 'Ayer';
  return new Intl.DateTimeFormat('es-GT', { timeZone, day: 'numeric', month: 'long' }).format(Date.parse(iso));
};

/** The time in the conversation list: the hour today, "Ayer", or "07/10" before that. */
export const listTimeLabel = (iso: string | null, timeZone: string, nowMs: number): string => {
  if (!iso) return '';
  const day = dayKey(Date.parse(iso), timeZone);
  const today = dayKey(nowMs, timeZone);
  if (day === today) return timeLabel(iso, timeZone);
  if (day === previousDay(today)) return 'Ayer';
  // Built from the day key: Intl does not zero-pad day and month in every engine.
  return `${day.slice(8, 10)}/${day.slice(5, 7)}`;
};

/** "05/10/2026 14:09", the date and time of an instant in the clinic's time zone. */
export const dateTimeLabel = (iso: string, timeZone: string): string => {
  const ms = Date.parse(iso);
  const day = dayKey(ms, timeZone);
  return `${day.slice(8, 10)}/${day.slice(5, 7)}/${day.slice(0, 4)} ${timeLabel(iso, timeZone)}`;
};
